import React from "react";
import { Link } from "react-router-dom";
import {
  FiServer,
  FiWifiOff,
  FiShieldOff,
  FiRefreshCcw,
  FiHome,
  FiChevronDown,
  FiChevronRight,
  FiTerminal,
  FiCopy,
  FiCheck,
  FiSearch,
  FiLock,
  FiClock,
  FiTool,
} from "react-icons/fi";
import "./ErrorBoundary.css";

// ─── Error classifier ─────────────────────────────────────────────────────────
function classifyError(error) {
  const msg = (error?.message || "").toLowerCase();
  const name = error?.name || "";
  const status = error?.status || error?.response?.status;

  // 401: Session Expired
  if (status === 401 || msg.includes("unauthorized") || msg.includes("session expired")) {
    return {
      variant: "session",
      code: "401",
      title: "Session Expired",
      description: "Your session has timed out for security. Please sign in again to continue your work.",
      Icon: FiLock,
      chipLabel: "AUTH_EXPIRED",
    };
  }

  // 403: Forbidden
  if (
    status === 403 ||
    msg.includes("permission") ||
    msg.includes("access denied") ||
    name === "ForbiddenError"
  ) {
    return {
      variant: "permission",
      code: "403",
      title: "Access Denied",
      description: "You don't have the required permissions to view this resource. Contact your administrator if you think this is a mistake.",
      Icon: FiShieldOff,
      chipLabel: "PERMISSION_ERROR",
    };
  }

  // 404: Not Found
  if (status === 404 || msg.includes("not found") || name === "NotFoundError") {
    return {
      variant: "notfound",
      code: "404",
      title: "Resource Not Found",
      description: "The page or resource you are looking for doesn't exist or has been moved to a new location.",
      Icon: FiSearch,
      chipLabel: "NOT_FOUND",
    };
  }

  // 429: Rate Limit
  if (status === 429 || msg.includes("too many requests") || msg.includes("rate limit")) {
    return {
      variant: "ratelimit",
      code: "429",
      title: "Too Many Requests",
      description: "You're moving a bit too fast! Please wait a moment before trying again.",
      Icon: FiClock,
      chipLabel: "RATE_LIMITED",
    };
  }

  // 502, 503, 504: Maintenance / Gateway
  if (
    [502, 503, 504].includes(status) ||
    msg.includes("bad gateway") ||
    msg.includes("service unavailable") ||
    msg.includes("gateway timeout")
  ) {
    return {
      variant: "maintenance",
      code: status?.toString() || "50X",
      title: "Temporary Service Issue",
      description: "The server is temporarily unable to handle your request. This is usually a temporary condition. Please try again in 30 seconds.",
      Icon: FiTool,
      chipLabel: "SERVER_BUSY",
    };
  }

  // Network / 0
  if (
    msg.includes("network") ||
    msg.includes("failed to fetch") ||
    msg.includes("net::err") ||
    (typeof navigator !== "undefined" && !navigator.onLine)
  ) {
    return {
      variant: "network",
      code: "OFFLINE",
      title: "Connection Lost",
      description: "Unable to reach the server. Please check your internet connection and try again.",
      Icon: FiWifiOff,
      chipLabel: "NETWORK_ERROR",
    };
  }

  // Default: something went wrong
  return {
    variant: "server",
    code: status?.toString() || "500",
    title: "Something Went Wrong",
    description: "We encountered an unexpected error while processing your request. Our team has been notified and is working to resolve it.",
    Icon: FiServer,
    chipLabel: "SYSTEM_ERROR",
  };
}

// ─── ErrorBoundary component ──────────────────────────────────────────────────
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      errorId: null,
      isDetailsOpen: false,
      isCopied: false,
    };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidUpdate(prevProps) {
    // Automatically reset error state if the resetKey (usually location.pathname) changes.
    // This allows the UI to recovery automatically when navigating away from a broken page,
    // WITHOUT force-unmounting the entire component tree (which causes redundant API calls).
    if (this.props.resetKey && prevProps.resetKey !== this.props.resetKey) {
      if (this.state.hasError) {
        this.setState({
          hasError: false,
          error: null,
          errorInfo: null,
          errorId: null,
          isDetailsOpen: false,
        });
      }
    }
  }

  componentDidCatch(error, errorInfo) {
    const errorId = `ERR-${Date.now()}-${Math.random()
      .toString(36)
      .substring(2, 9)
      .toUpperCase()}`;

    this.setState({ error, errorInfo, errorId });

    // Dev console
    if (process.env.NODE_ENV === "development") {
      console.error(`[${errorId}] ErrorBoundary caught:`, error, errorInfo);
    }

    // localStorage backup (last 10)
    try {
      const log = {
        errorId,
        message: error.toString(),
        componentStack: errorInfo?.componentStack,
        timestamp: new Date().toISOString(),
        url: window.location.href,
      };
      const existing = JSON.parse(localStorage.getItem("admin_errorLogs") || "[]");
      existing.push(log);
      localStorage.setItem("admin_errorLogs", JSON.stringify(existing.slice(-10)));
    } catch (_) { /* silent */ }
  }

  handleRetry = () => {
    if (this.props.onReset) {
      this.setState({ hasError: false, error: null, errorInfo: null, errorId: null, isDetailsOpen: false });
      this.props.onReset();
    } else {
      // Default behavior for global boundary: fresh reload
      window.location.reload();
    }
  };

  handleGoHome = () => {
    this.setState({ hasError: false, error: null, errorInfo: null, errorId: null, isDetailsOpen: false });
    // Navigation is handled by <Link>, but we clear state to prevent immediate re-crash
    if (this.props.onReset) this.props.onReset();
  };

  handleCopyLog = () => {
    const { error, errorInfo, errorId } = this.state;
    const log = [
      `ERROR_ID:   ${errorId}`,
      `TIMESTAMP:  ${new Date().toISOString()}`,
      `URL:        ${window.location.href}`,
      "",
      error?.toString(),
      "",
      errorInfo?.componentStack,
    ].join("\n");

    navigator.clipboard?.writeText(log).then(() => {
      this.setState({ isCopied: true });
      setTimeout(() => this.setState({ isCopied: false }), 2500);
    });
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    const cfg = classifyError(this.state.error);
    const { Icon } = cfg;
    const { isDetailsOpen, isCopied, errorId, error, errorInfo } = this.state;

    // We removed DOMPurify since strings are hardcoded safely in classifyError
    const safeDescription = cfg.description;
    const stackTrace = `${error?.toString() ?? ""}\n\n${errorInfo?.componentStack ?? ""}`;

    return (
      <div className={`error-boundary-wrapper variant-${cfg.variant}`}>
        <div className="error-container">
          <div className="error-card">

            {/* Icon */}
            <div className="error-icon">
              <Icon strokeWidth={1.6} />
            </div>

            {/* Error code */}
            <p className="error-code">{cfg.code}</p>

            {/* Title */}
            <h1 className="error-title">{cfg.title}</h1>

            {/* Description */}
            <p className="error-description">
                {safeDescription}
            </p>

            {/* Error ID chip */}
            {errorId && (
              <div className="error-id-chip">
                <span className="dot" />
                {errorId}
              </div>
            )}

            {/* Action buttons */}
            <div className="error-actions">
              <Link to="/" onClick={this.handleGoHome} className="eb-btn-primary">
                <FiHome size={18} strokeWidth={2} />
                Back to Dashboard
              </Link>

              <button onClick={this.handleRetry} className="eb-btn-secondary">
                <FiRefreshCcw size={18} strokeWidth={2} />
                Try Again
              </button>
            </div>

            {/* Technical details */}
            <div className="error-details">
              <button
                className="error-details-toggle"
                onClick={() => this.setState(p => ({ isDetailsOpen: !p.isDetailsOpen }))}
              >
                <span style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}>
                  {isDetailsOpen
                    ? <FiChevronDown size={13} strokeWidth={2.5} />
                    : <FiChevronRight size={13} strokeWidth={2.5} />}
                  <FiTerminal size={12} strokeWidth={2.5} />
                  Technical Details
                </span>

                {isDetailsOpen && (
                  <span className="toggle-actions">
                    <button
                      className={`copy-btn${isCopied ? " copied" : ""}`}
                      onClick={e => { e.stopPropagation(); this.handleCopyLog(); }}
                    >
                      {isCopied ? <FiCheck size={12} /> : <FiCopy size={12} />}
                      {isCopied ? "Copied!" : "Copy Log"}
                    </button>
                  </span>
                )}
              </button>

              {isDetailsOpen && (
                <div className="error-stack">
                  <pre>{stackTrace}</pre>
                </div>
              )}
            </div>

          </div>
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;